import { Injectable } from '@angular/core';
import { HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IFileHydrated, IFileUploadItem } from '@newroom-connect/library/interfaces';

import { BroadcastService } from '../broadcast/broadcast.service';

import { FileService } from './file.service';

export enum FileBroadcastEvent {
  UPLOADED_FILE = 'uploadedFile',
  REPLACED_FILE = 'replacedFile',
  REPLACE_FILE = 'replaceFile',
  UPDATED_FILE = 'updatedFile',
  UPDATE_FILE = 'updateFile'
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService extends FileService {
  /**
   * @constructor
   *
   * @param broadcastService
   */
  constructor(private readonly broadcastService: BroadcastService) {
    super();
  }

  /**
   * Upload a file to the specified project.
   *
   * @param projectId - The ID of the project to upload the file to.
   * @param file - The file to upload.
   * @param isPublic - Optional flag to mark the file as public.
   *
   * @returns Observable of the upload progress and response.
   */
  public uploadFile(projectId: string, file: File, isPublic?: boolean): Observable<HttpEvent<any>> {
    const formData = new FormData();

    formData.append('file', file);

    // Add required headers for file validation
    const headers = new HttpHeaders({
      'x-content-type': file.type,
      'x-filename': file.name,
      ...(isPublic && { 'x-is-public': 'true' })
    });

    return this.apiService.postWithReportedProgress<any>(
      `projects/${projectId}/file-upload?ngsw-bypass=true`,
      formData,
      { headers }
    );
  }

  /**
   * Replace an existing file with a new one.
   *
   * @param projectId - The ID of the project containing the file.
   * @param fileId - The ID of the file to replace.
   * @param file - The new file to replace the existing one.
   * @param trackingId - Optional tracking ID for SSE progress tracking.
   *
   * @returns Observable of the upload progress and response.
   */
  public replaceFile(projectId: string, fileId: string, file: File, trackingId?: string): Observable<HttpEvent<any>> {
    const formData = new FormData();

    formData.append('file', file);

    // Add required headers for file validation
    const headersConfig: Record<string, string> = {
      'x-content-type': file.type,
      'x-filename': file.name
    };

    // Add tracking ID header if provided
    if (trackingId) {
      headersConfig['x-tracking-id'] = trackingId;
    }

    const headers = new HttpHeaders(headersConfig);

    return this.apiService.postWithReportedProgress<any>(
      `projects/${projectId}/file-upload/${fileId}/replace?ngsw-bypass=true`,
      formData,
      { headers }
    );
  }

  /**
   *
   * @param fileUploadEvent
   * @param fileUploadItem
   * @param eventType
   * @returns
   */
  public handleFileUploadEvent(fileUploadEvent: HttpEvent<any>, fileUploadItem: IFileUploadItem, eventType: FileBroadcastEvent): IFileHydrated | void {
    switch (fileUploadEvent.type) {
      case HttpEventType.Sent:
        fileUploadItem.status.set('in-progress');

        this.logger.debug('File upload request has been made.');

        break;
      case HttpEventType.ResponseHeader:
        this.logger.debug('File upload response header has been received.');
        break;
      case HttpEventType.UploadProgress:
        fileUploadItem.progress.set(Math.round(fileUploadEvent.loaded / (fileUploadEvent.total ?? Number.MAX_SAFE_INTEGER) * 100));

        this.logger.debug(`Uploaded ${fileUploadItem.progress()}%...`);

        break;
      case HttpEventType.Response:
        this.logger.debug('File upload was successful', fileUploadEvent.body, eventType);

        // Wrap the broadcast in a setTimeout to ensure that the file is fully uploaded before broadcasting.
        setTimeout(() => {
          fileUploadItem.status.set('success');
          this.broadcastService.emitBroadcast<IFileHydrated>({
            name: eventType,
            state: FileService.hydrateFile(fileUploadEvent.body)
          });
        }, 500);

        return FileService.hydrateFile(fileUploadEvent.body);
      default:
        break;
    }
  }
}
